import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import Modal from '../components/Modal';
import Button from '../components/Button';
import TableScrollContainer from '../components/TableScrollContainer';
import './Clientes.css';

function formatPhone(telefone = '') {
  if (!telefone) return '';
  const digits = String(telefone).replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ nome: '', telefone: '', instagram: '' });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    try {
      const response = await axios.get('/api/clientes');
      setClientes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasTelefone = String(formData.telefone || '').replace(/\D/g, '').length > 0;
    const hasInstagram = String(formData.instagram || '').trim().length > 0;

    if (!hasTelefone && !hasInstagram) {
      alert('Informe ao menos Telefone ou Instagram.');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`/api/clientes/${editingId}`, formData);
      } else {
        await axios.post('/api/clientes', formData);
      }
      fetchClientes();
      setShowModal(false);
      setFormData({ nome: '', telefone: '', instagram: '' });
      setEditingId(null);
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      alert(error.response?.data?.error || 'Não foi possível salvar o cliente.');
    }
  };

  const handleEdit = (cliente) => {
    setFormData({
      nome: cliente.nome || '',
      telefone: cliente.telefone || '',
      instagram: cliente.instagram || '',
    });
    setEditingId(cliente.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza?')) {
      try {
        await axios.delete(`/api/clientes/${id}`);
        fetchClientes();
      } catch (error) {
        console.error('Erro ao deletar cliente:', error);
      }
    }
  };

  const filteredClientes = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return clientes;
    }

    return clientes.filter((cliente) => {
      const nome = String(cliente.nome || '').toLowerCase();
      const telefone = String(cliente.telefone || '');
      const instagram = String(cliente.instagram || '').toLowerCase();

      return nome.includes(query) || telefone.includes(query) || instagram.includes(query);
    });
  }, [clientes, searchTerm]);

  return (
    <div className="clientes">
      <div className="header-section">
        <h1>Clientes</h1>
        <Button
          variant="primary"
          onClick={() => {
            setFormData({ nome: '', telefone: '', instagram: '' });
            setEditingId(null);
            setShowModal(true);
          }}
        >
          <FiPlus /> Novo Cliente
        </Button>
      </div>

      {loading ? (
        <p className="loading">Carregando...</p>
      ) : (
        <div className="card">
          <div className="clientes-toolbar">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar por nome, telefone ou Instagram"
            />
            <span>{filteredClientes.length} cliente(s)</span>
          </div>

          <TableScrollContainer rows={filteredClientes.length} threshold={10} maxHeight="min(72vh, 760px)">
            <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Telefone</th>
                <th>Instagram</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredClientes.map((cliente) => (
                <tr key={cliente.id}>
                  <td>{cliente.nome}</td>
                  <td>{formatPhone(cliente.telefone)}</td>
                  <td>{cliente.instagram || '-'}</td>
                  <td className="actions">
                    <button
                      className="edit-btn"
                      onClick={() => handleEdit(cliente)}
                      title="Editar"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(cliente.id)}
                      title="Deletar"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
              {filteredClientes.length === 0 && (
                <tr>
                  <td colSpan="4" className="empty-state">Nenhum cliente encontrado.</td>
                </tr>
              )}
            </tbody>
            </table>
          </TableScrollContainer>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingId(null);
          setFormData({ nome: '', telefone: '', instagram: '' });
        }}
        title={editingId ? 'Editar Cliente' : 'Novo Cliente'}
      >
        <form onSubmit={handleSubmit} className="form">
          <label>
            Nome *
            <input
              type="text"
              required
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              placeholder="Nome completo"
            />
          </label>

          <label>
            Telefone
            <input
              type="tel"
              value={formData.telefone}
              onChange={(e) => setFormData({ ...formData, telefone: formatPhone(e.target.value) })}
              placeholder="(67) 99999-9999"
            />
          </label>

          <label>
            Instagram
            <input
              type="text"
              value={formData.instagram || ''}
              onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
              placeholder="@usuario"
            />
          </label>

          <small>Preencha ao menos um contato: Telefone ou Instagram.</small>

          <div className="form-actions">
            <Button type="submit" variant="primary">
              Salvar
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditingId(null);
                setFormData({ nome: '', telefone: '', instagram: '' });
              }}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Clientes;
